// src/hooks/useCanteenSocket.js
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { setOrders, setRealtimeOrders } from "store/slices/orderSlice";
import voice from "assets/voice/notifcation.mp3";
import { setCanteenSettings } from "store/slices/settingsSlice";

const useCanteenSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const token = useSelector((s) => s.auth.token);

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Sound refs
  const sounds = useRef({
    newOrder: new Audio(voice),
    statusUpdate: new Audio(voice),
    stockBack: new Audio(voice),
    outOfStock: new Audio(voice),
  });

  const API_SOCKET_URL =
    process.env.REACT_APP_SOCKET_BASE_URL || "ws://192.168.29.18:8000";

  // Set volume once
  useEffect(() => {
    Object.values(sounds.current).forEach((audio) => {
      audio.volume = 0.8;
    });
  }, []);

  // Respect global sound setting
  const isSoundEnabled = () =>
    localStorage.getItem("notificationSoundEnabled") !== "false";

  const playSound = (type) => {
    if (!isSoundEnabled()) return;
    const audio = sounds.current[type];
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    }
  };

  useEffect(() => {
    if (!user?.id || !token) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }

    const wsUrl = `${API_SOCKET_URL}/canteen/?token=${token}`;
    let shouldReconnect = true;

    const connect = () => {
      if (!shouldReconnect) return;

      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log("Canteen WebSocket Connected");
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          const extractOrder = (payload) => {
            if (payload?.order) return payload.order;
            if (payload?.data?.order) return payload.data.order;
            if (payload?.payload) return payload.payload;
            return payload;
          };

          const order = extractOrder(msg);

          // 1. INITIAL STATE — no sound
          if (msg.type === "initial_state") {
            let orders = [];
            if (msg.is_admin === true) {
              orders = msg.pending_orders || [];
            } else {
              orders = msg.my_orders || [];
            }

            dispatch(
              setOrders({
                orders,
                stats: msg.stats,
                pagination: {
                  count: orders.length,
                  page_size: orders.length,
                  next: null,
                  previous: null,
                },
              })
            );

            // Also sync settings on connect
            if (msg.settings?.can_accept_orders !== undefined) {
              dispatch(
                setCanteenSettings({
                  can_accept_orders: msg.settings.can_accept_orders,
                })
              );
            }

            return;
          }

          // 2. SETTINGS UPDATE (REAL-TIME FOR ALL USERS)
          if (msg.type === "settings_updated") {
            const newStatus = msg.can_accept_orders;
            const updatedBy = msg.updated_by || "Someone";

            dispatch(setCanteenSettings({ can_accept_orders: newStatus }));

            // Show toast to everyone except the one who changed it (optional)
            if (user?.firstName + " " + user?.lastName !== updatedBy) {
              toast[newStatus ? "success" : "warning"](
                newStatus
                  ? `✅ Canteen is now accepting orders!`
                  : `⛔ Canteen stopped accepting orders`,
                {
                  description: `Updated by ${updatedBy}`,
                  duration: 5000,
                }
              );
              playSound(newStatus ? "newOrder" : "outOfStock");
            }
            return;
          }

          // 2. ADMIN: New Order Alert
          if (msg.type === "canteen_update") {
            const data = msg.data || {};c
            const order = data.order || data.payload;

            if (!order?.id) return;

            if (
              data.event === "order_created" ||
              data.type === "canteen_new_order_alert"
            ) {
              toast.success(
                `New Order → ${order.employee_name} • ₹${order.total_price}`,
                {
                  duration: 6000,
                  icon: "New Order",
                }
              );
              playSound("newOrder"); // Loud & exciting!
              dispatch(setRealtimeOrders(order));
            } else if (data.event === "order_updated") {
              const shortId = order.id.slice(0, 8);
              toast.info(`#${shortId} → ${order.status.toUpperCase()}`, {
                duration: 4000,
              });
              playSound("statusUpdate");
              dispatch(setRealtimeOrders(order));
            } else if (data.event === "order_cancelled") {
              toast.warning("Order cancelled");
              playSound("outOfStock"); // Reuse or make a cancel sound
              dispatch(setRealtimeOrders({ ...order, status: "cancelled" }));
            }
            return;
          }

          // 3. EMPLOYEE: Direct events
          if (msg.type === "order_created" || msg.type === "order.created") {
            if (!order?.id) return;
            toast.success(`Order placed successfully! • ₹${order.total_price}`);
            playSound("newOrder");
            dispatch(setRealtimeOrders(order));
            return;
          }

          if (msg.type === "order_updated") {
            if (!order?.id) return;
            const shortId = order.id.slice(0, 8);
            // toast.info(
            //   `Your order #${shortId} → ${order.status.toUpperCase()}`,
            //   { duration: 5000 }
            // );
            playSound("statusUpdate");
            dispatch(setRealtimeOrders(order));
            return;
          }

          if (
            msg.type === "order_cancelled" ||
            msg.type === "order.cancelled"
          ) {
            toast.warning("Your order was cancelled");
            playSound("outOfStock");
            dispatch(setRealtimeOrders({ ...order, status: "cancelled" }));
            return;
          }

          // STOCK CHANGES
          if (
            msg.type === "menu_stock_changed" ||
            msg.type === "canteen_menu_stock_changed"
          ) {
            const { name, in_stock } = msg;

            dispatch({
              type: "canteen/toggleMenuStock/fulfilled",
              payload: { id: msg.menu_id, name, in_stock },
            });

            if (in_stock) {
              toast.success(`${name} is now back in stock!`, {
                icon: "✅",
              });
              playSound("stockBack");
            } else {
              toast.warning(`${name} is out of stock`, {
                icon: "❌",
              });
              playSound("outOfStock");
            }
            return;
          }

          // Fallback
          if (order?.id) {
            dispatch(setRealtimeOrders(order));
          }
        } catch (err) {
          console.error("Canteen Socket Parse Error:", err);
        }
      };

      socket.onclose = (event) => {
        console.log("Canteen WS closed", event.code, event.reason);
        socketRef.current = null;

        if (shouldReconnect) {
          console.log("Reconnecting canteen socket in 3s...");
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };

      socket.onerror = (err) => {
        console.error("Canteen WS Error:", err);
        socket.close();
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user?.id, token, dispatch]);
};

export default useCanteenSocket;
